// Project setup: what a project's model finds and where it runs. An admin chooses both (api/projects.py
// set_project_setup); training follows them, and the export dialog starts from the target.
import type { DeployTarget, Detect, Project } from "../api/types";

export interface Choice<T> { value: T; title: string; text: string; feature?: string; note?: string }

export const DETECT_CHOICES: Choice<Detect>[] = [
  { value: "boxes", title: "Boxes around objects", text: "Finds each object and draws a rectangle around it. Right for most inspection and counting." },
  { value: "corners", title: "The four corners of a face", text: "Places the four corners of each package face, so length and breadth can be measured. Annotate with the polygon tool." },
  { value: "outlines", title: "The exact outline", text: "Traces the edge of each object, whatever its shape. Annotate with the polygon tool.",
    note: "Outline models annotate inside Model Bench but cannot be exported for deployment yet." },
];

export const TARGET_CHOICES: Choice<DeployTarget>[] = [
  { value: "onnxruntime", title: "ctrlX or a PC (CPU)", text: "The ctrlX vision app, or any computer running ONNX Runtime." },
  { value: "tensorrt", title: "NVIDIA GPU", text: "An industrial PC with an NVIDIA graphics card.", feature: "nvidia_export" },
  { value: "hailo", title: "Hailo accelerator", text: "A ctrlX CORE with a Hailo-8 chip. Costs a little accuracy; needs compiling before it runs.", feature: "hailo" },
];

const SHORT_DETECT: Record<Detect, string> = { boxes: "boxes", corners: "four corners", outlines: "outlines" };
const SHORT_TARGET: Record<DeployTarget, string> = { onnxruntime: "ctrlX or a PC (CPU)", tensorrt: "an NVIDIA GPU", hailo: "a Hailo accelerator" };

/** Whether an admin has set the project up. */
export const isSetUp = (p: Pick<Project, "detect" | "deploy_target"> | null | undefined) => !!p?.detect && !!p?.deploy_target;

/** The setup in effect: the project's, or what training does for a project nobody has set up. */
export function effectiveSetup(p: Pick<Project, "detect" | "deploy_target"> | null | undefined): { detect: Detect; deploy_target: DeployTarget } {
  return { detect: p?.detect ?? "boxes", deploy_target: p?.deploy_target ?? "onnxruntime" };
}

/** "Finds boxes · runs on ctrlX or a PC (CPU)". */
export function setupLine(p: Pick<Project, "detect" | "deploy_target"> | null | undefined): string {
  const s = effectiveSetup(p);
  return `Finds ${SHORT_DETECT[s.detect]} · runs on ${SHORT_TARGET[s.deploy_target]}`;
}

/** The architecture options a training run gets from the setup (the server applies the same rule). */
export function trainingOptions(p: Pick<Project, "detect" | "deploy_target"> | null | undefined): { corners: boolean; masks: boolean; hailo_safe: boolean } {
  const s = effectiveSetup(p);
  return { corners: s.detect === "corners", masks: s.detect === "outlines", hailo_safe: s.deploy_target === "hailo" };
}
